import { ThemeProvider, useMediaQuery } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { Suspense, useMemo, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { ColorModeContext } from 'styles/ThemeSwitcher';
import { AuthProvider } from './auth';
import ErrorBoundary from './components/error/ErrorBoundary';
import FullPageCircularProgress from './components/FullPageCircularProgress';
import { store } from './redux/store';
import router from './router';
import theme from './styles/theme';
import { LocalStorageKey } from './vendor/local';
import './styles/utils.css';

type ThemeType = 'light' | 'dark';

function App() {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const lastSelectedTheme = localStorage.getItem(LocalStorageKey.Theme) as ThemeType | null;
  const [mode, setMode] = useState<ThemeType>(lastSelectedTheme || (prefersDarkMode ? 'dark' : 'light'));
  const colorMode = useMemo(() => ({
    mode,
    toggleColorMode: () =>
      setMode(prevMode => {
        const nextMode = prevMode === 'light' ? 'dark' : 'light';
        localStorage.setItem(LocalStorageKey.Theme, nextMode);
        return nextMode;
      }),
    setColorMode: (nextMode: ThemeType) => {
      localStorage.setItem(LocalStorageKey.Theme, nextMode);
      setMode(nextMode);
    }
  }), [mode]);

  const currentTheme = useMemo(() => createTheme({
    ...theme(mode),
    cssVariables: { cssVarPrefix: 'inspectify' },
  }), [mode]);

  return (
    <Suspense fallback={<FullPageCircularProgress />}>
      <ErrorBoundary>
        <Provider store={store}>
          <AuthProvider>
            <ColorModeContext.Provider value={colorMode}>
              <ThemeProvider theme={currentTheme}>
                <CssBaseline />
                <HelmetProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <RouterProvider
                      router={router}
                      future={{
                        v7_startTransition: true,
                      }}
                    />
                  </LocalizationProvider>
                </HelmetProvider>
              </ThemeProvider>
            </ColorModeContext.Provider>
          </AuthProvider>
        </Provider>
      </ErrorBoundary>
    </Suspense>
  );
}

export default App;
